/*
 * Selectors.
 */

import {isEqual} from 'lodash';
import memoizeOne from 'memoize-one';

import {AppParametricSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {
  AdminSetupGuideTasksAndProgress,
  AdminSetupGuideTaskSelectorParams,
  FluSetupGuideTasksAndProgress,
  FluSetupGuideTaskSelectorParams,
  SetupGuideScreenTasksAndProgress,
  SetupGuideTaskDefinition,
} from './onboardingTypes';

/*
 * Selectors.
 */

export const getAdminOnboardingTasks: AppParametricSelector<
  AdminSetupGuideTaskSelectorParams,
  AdminSetupGuideTasksAndProgress
> = memoizeOne(
  (state, params: AdminSetupGuideTaskSelectorParams) => ({
    demos: computeTasksAndProgress(params.taskDefinitions.demos, (definition) =>
      definition.getIsTaskComplete(state, params),
    ),
    setup: computeTasksAndProgress(params.taskDefinitions.setup, (definition) =>
      definition.getIsTaskComplete(state, params),
    ),
  }),
  ([nextState, nextParams], [prevState, prevParams]) =>
    nextState === prevState && isEqual(nextParams, prevParams),
);

export const getFluOnboardingTasks: AppParametricSelector<
  FluSetupGuideTaskSelectorParams,
  FluSetupGuideTasksAndProgress
> = memoizeOne(
  (state, params: FluSetupGuideTaskSelectorParams) => ({
    start: computeTasksAndProgress(params.taskDefinitions.start, (definition) =>
      definition.getIsTaskComplete(state, params),
    ),
  }),
  ([nextState, nextParams], [prevState, prevParams]) =>
    nextState === prevState && isEqual(nextParams, prevParams),
);

function computeTasksAndProgress<T extends SetupGuideTaskDefinition>(
  taskDefinitions: ReadonlyArray<T>,
  computeIsComplete: (taskDefinitions: T) => boolean | undefined,
): SetupGuideScreenTasksAndProgress {
  const tasks = taskDefinitions.map((definition, i) => ({
    key: definition.key,
    title: definition.title,
    description: definition.description,
    details: definition.details,
    isExternal: Boolean(definition.isExternal),
    number: definition.isUnnumbered ? undefined : i + 1,
    isComplete: computeIsComplete(definition),
    startTask: definition.startTask,
  }));

  // TODO: Reconsider whether we really need this derived/denormalized state.
  const progress = {
    totalSteps: tasks.length,
    completedSteps: tasks.reduce((accum, t) => (t.isComplete ? accum + 1 : accum), 0),
    hasCompletedAllTasks: tasks.every((t) => t.isComplete),
  };

  return {
    tasks,
    progress,
  };
}
